import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { useColors } from '../../../hooks/useColors';
import { useTextReplacement } from '../../../hooks/useTextReplacement';
import { ActionButton } from '../forms';

/**
 * Notification component for displaying success and error messages
 * @param {Object} props - Component props
 * @param {string} props.message - Message to display
 * @param {string} props.type - Type of notification ('success', 'error', 'warning', 'info')
 * @param {boolean} props.open - Whether the notification is open
 * @param {Function} props.onClose - Function to call when notification is closed
 * @param {number} props.duration - Duration in milliseconds to show the notification
 * @param {string} props.position - Position of notification ('top-right', 'top-left', 'bottom-right', 'bottom-left')
 */
const Notification = ({ 
  message, 
  type = 'success', 
  open = false, 
  onClose, 
  duration = 5000,
  position = 'top-right'
}) => {
  const { semantic } = useColors();
  const { replace } = useTextReplacement();
  const [visible, setVisible] = useState(open);
  
  // Update visibility when open prop changes
  useEffect(() => {
    setVisible(open);
  }, [open]);
  
  // Auto-hide after duration
  useEffect(() => {
    if (visible && duration > 0) {
      const timer = setTimeout(() => {
        handleClose();
      }, duration);
      
      return () => clearTimeout(timer);
    }
  }, [visible, duration]);
  
  // Handle close
  const handleClose = () => {
    setVisible(false);
    if (onClose) {
      onClose();
    }
  };
  
  // Get background and text colors based on type
  const getColors = () => {
    switch (type) {
      case 'success':
        return {
          background: semantic.successBg,
          text: semantic.successText
        };
      case 'error':
        return {
          background: semantic.errorBg,
          text: semantic.errorText
        };
      case 'warning':
        return {
          background: semantic.warningBg,
          text: semantic.warningText
        };
      case 'info':
      default:
        return {
          background: semantic.sectionBg,
          text: semantic.formText
        };
    }
  };
  
  // Get position classes
  const getPositionClasses = () => {
    switch (position) {
      case 'top-left':
        return 'top-4 left-4';
      case 'top-right':
        return 'top-4 right-4';
      case 'bottom-left':
        return 'bottom-4 left-4';
      case 'bottom-right':
        return 'bottom-4 right-4';
      default:
        return 'top-4 right-4';
    }
  };
  
  const { background, text } = getColors();
  
  if (!visible) return null;
  
  return (
    <div 
      className={`fixed ${getPositionClasses()} z-50 transition-all duration-300 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}
      role="alert"
      aria-live={type === 'error' ? 'assertive' : 'polite'}
    >
      <div className={`${background} ${text} p-3 rounded-md shadow-lg flex items-center justify-between min-w-[300px] max-w-[500px]`}>
        <div className="flex-grow pr-2">{replace(message)}</div>
        <ActionButton
          action="close"
          onClick={handleClose}
          title={replace('Close notification')}
          size="sm"
          className="flex-shrink-0"
        />
      </div>
    </div>
  );
};

Notification.propTypes = {
  message: PropTypes.string.isRequired,
  type: PropTypes.oneOf(['success', 'error', 'warning', 'info']),
  open: PropTypes.bool,
  onClose: PropTypes.func,
  duration: PropTypes.number,
  position: PropTypes.oneOf(['top-right', 'top-left', 'bottom-right', 'bottom-left'])
};

export default Notification;

